import type { FC, ReactNode } from 'react'

import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { PixelButton } from '@/components/generals/pixel-button'

import { NAV_LINKS } from '@/constants/content'
import {
    ICON_MENU,
    ICON_PLUS,
    LOGO_MARK,
    LOGO_PALETTE,
    LOGO_PALETTE_DARK
} from '@/constants/sprites'

interface MobileMenuProps {
    active: string | null
    onClose: () => void
}

interface LogoProps {
    className?: string
    dark?: boolean
}

const TRACKED_SECTIONS = ['hero', 'about', 'who-we-are', 'services', 'services-for', 'process', 'team', 'contacts']

const PROGRESS_BLOCKS = 48

const useActiveSection = (ids: ReadonlyArray<string>): string | null => {
    const [active, setActive] = useState<string | null>(null)

    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return

        const nodes = ids
            .map(id => document.getElementById(id))
            .filter((node): node is HTMLElement => Boolean(node))

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActive(entry.target.id)
            })
        }, { rootMargin: '-45% 0px -50% 0px' })

        nodes.forEach(node => observer.observe(node))

        return () => observer.disconnect()
    }, [ids])

    return active
}

export const Logo: FC<LogoProps> = ({ className, dark = false }): ReactNode => {
    return (
        <span className={cn('flex items-center gap-2.5', className)}>
            <PixelSprite
                rows={LOGO_MARK}
                palette={dark ? LOGO_PALETTE_DARK : LOGO_PALETTE}
                className='h-8 w-auto transition-transform duration-200 ease-[steps(2,end)] group-hover:-rotate-6'
            />
            <span className='font-heading text-[1.65rem] leading-none tracking-tight'>
                dinolab
            </span>
        </span>
    )
}

const MobileMenu: FC<MobileMenuProps> = ({ active, onClose }): ReactNode => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useLayoutEffect(() => {
        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [onClose])

    return (
        <div
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label="Site menu"
            className='fixed inset-0 z-[60] flex animate-pixel-wipe flex-col bg-bubblegum pattern-dots lg:hidden'
        >
            <div className='flex h-16 items-center justify-between border-b-2 border-ink px-5'>
                <a href='#hero' onClick={onClose} className='group' aria-label="dinolab, back to top">
                    <Logo />
                </a>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='grid size-11 place-items-center border-2 border-ink bg-white shadow-pixel-sm'
                >
                    <span className='sr-only'>Close menu</span>
                    <PixelSprite rows={ICON_PLUS} className='size-4 rotate-45' />
                </button>
            </div>

            <nav aria-label="Mobile" className='flex flex-1 flex-col justify-center px-6'>
                <ul className='space-y-1'>
                    {NAV_LINKS.map((link, index) => (
                        <li
                            key={link.href}
                            className='animate-letter-pop'
                            style={{ animationDelay: `${160 + index * 70}ms` }}
                        >
                            <a
                                href={link.href}
                                onClick={onClose}
                                aria-current={active === link.href.slice(1) ? 'true' : undefined}
                                className='group flex items-center gap-4 py-1.5 font-heading text-[clamp(2.5rem,12vw,3.75rem)] leading-none text-ink'
                            >
                                <span className='font-sans text-sm font-bold tabular-nums'>
                                    0{index + 1}
                                </span>
                                <span className='underline-offset-8 group-hover:underline group-aria-[current=true]:underline'>
                                    {link.label}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='border-t-2 border-ink px-6 py-6'>
                <PixelButton href='#contacts' onClick={onClose} variant='paper' size='lg' arrow className='w-full'>
                    let&apos;s start!
                </PixelButton>
            </div>
        </div>
    )
}

const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [progress, setProgress] = useState(0)
    const [open, setOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)
    const active = useActiveSection(TRACKED_SECTIONS)

    useEffect(() => {
        let frame = 0

        const update = () => {
            frame = 0

            const max = document.documentElement.scrollHeight - window.innerHeight
            const ratio = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0

            setScrolled(window.scrollY > 16)
            setProgress(Math.round(ratio * PROGRESS_BLOCKS))
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            window.cancelAnimationFrame(frame)
        }
    }, [])

    const closeMenu = useRef(() => {
        setOpen(false)
        window.requestAnimationFrame(() => menuButtonRef.current?.focus({ preventScroll: true }))
    }).current

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 border-b-2 transition-colors duration-300',
                    scrolled ? 'border-ink bg-paper/90 backdrop-blur-md' : 'border-transparent bg-transparent'
                )}
            >
                <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 md:h-[72px] md:px-8'>
                    <a href='#hero' className='group shrink-0' aria-label="dinolab, back to top">
                        <Logo />
                    </a>

                    <nav aria-label="Main" className='hidden lg:block'>
                        <ul className='flex items-center gap-8'>
                            {NAV_LINKS.map(link => {
                                const isActive = active === link.href.slice(1)

                                return (
                                    <li key={link.href}>
                                        <a
                                            href={link.href}
                                            aria-current={isActive ? 'true' : undefined}
                                            className={cn(
                                                'relative py-2 text-[13px] font-semibold tracking-wide text-ink',
                                                'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-[3px] after:origin-left after:bg-bubblegum',
                                                'after:transition-transform after:duration-300 after:ease-[steps(5,end)] hover:after:scale-x-100',
                                                isActive ? 'after:scale-x-100' : 'after:scale-x-0'
                                            )}
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-3'>
                        <PixelButton href='#contacts' size='sm' className='hidden sm:inline-flex'>
                            let&apos;s start!
                        </PixelButton>

                        <button
                            ref={menuButtonRef}
                            type='button'
                            onClick={() => setOpen(true)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            className='grid size-11 place-items-center border-2 border-ink bg-white shadow-pixel-sm transition-[translate,box-shadow] duration-150 ease-[steps(2,end)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none lg:hidden'
                        >
                            <span className='sr-only'>Open menu</span>
                            <PixelSprite rows={ICON_MENU} className='h-3.5 w-auto' />
                        </button>
                    </div>
                </div>

                <div aria-hidden='true' className='absolute inset-x-0 -bottom-[2px] h-1'>
                    <div
                        className='h-full bg-[repeating-linear-gradient(90deg,var(--bubblegum)_0_10px,transparent_10px_12px)]'
                        style={{ width: `${(progress / PROGRESS_BLOCKS) * 100}%` }}
                    />
                </div>
            </header>

            {open && <MobileMenu active={active} onClose={closeMenu} />}
        </>
    )
}

export default SiteHeader
